import { z } from 'zod'

/**
 * Exportação e importação XLSX (4.10), de contatos e de empresas (ADR 0039).
 *
 * Cada lado tem a sua permissão (`contatos.importar`, `empresas.exportar`...,
 * ADR 0038), e quem não vê a base inteira exporta e atualiza só a própria
 * carteira. Cada planilha tem um modelo para baixar, com uma linha de exemplo
 * e uma aba "como preencher" que explica coluna por coluna.
 */

// ─────────────────────────── contatos ───────────────────────────

/** Cabeçalhos fixos do modelo de contatos. Campos customizados entram depois destes. */
export const COLUNAS_FIXAS = [
  'nome',
  'telefone',
  'documento',
  'empresa_cnpj',
  'vendedor_email',
  'funil',
  'etapa',
  'valor',
  'etiquetas',
] as const

export type ColunaFixa = (typeof COLUNAS_FIXAS)[number]

/**
 * Colunas que a exportação traz só para leitura. Na importação são ignoradas
 * em silêncio: quem exporta, mexe e importa de volta não precisa apagar nada.
 */
export const COLUNAS_SO_DE_LEITURA_CONTATOS = ['empresa', 'criado_em'] as const

export const DESCRICAO_COLUNA: Record<ColunaFixa, string> = {
  nome: 'Obrigatório. Nome do contato.',
  telefone: 'Com DDD. Aceita (31) 99999-6935, 31999996935 ou +5531999996935.',
  documento: 'CPF ou CNPJ do contato, com ou sem pontuação. É por ele que o contato é achado.',
  empresa_cnpj:
    'CNPJ de uma empresa já cadastrada: suba a planilha de empresas antes. O contato passa para o dono da empresa.',
  vendedor_email:
    'E-mail de um usuário ativo. Em branco = contato sem dono. Contato com empresa segue o dono da empresa.',
  funil: 'Nome exato do funil. Em branco = contato fora do funil.',
  etapa:
    'Nome exato da etapa dentro do funil. Etapa de ganho ou de perda não é aceita: ganho e perda só pelo quadro de Leads, que pede o valor e o motivo.',
  valor:
    'Valor da negociação, com vírgula nos centavos: 1500,00. A célula de número do Excel também serve.',
  etiquetas: 'Nomes separados por ponto e vírgula. Etiqueta inexistente é ignorada.',
}

// ─────────────────────────── empresas ───────────────────────────

export const COLUNAS_EMPRESA = [
  'nome',
  'cnpj',
  'razao_social',
  'telefone',
  'email',
  'site',
  'endereco',
  'bairro',
  'cidade',
  'uf',
  'cep',
  'vendedor_email',
  'etiquetas',
  'observacoes',
] as const

export type ColunaEmpresa = (typeof COLUNAS_EMPRESA)[number]

/** Só leitura na exportação de empresas: quantos contatos a empresa tem. */
export const COLUNAS_SO_DE_LEITURA_EMPRESAS = ['contatos', 'criado_em'] as const

export const DESCRICAO_COLUNA_EMPRESA: Record<ColunaEmpresa, string> = {
  nome: 'Obrigatório. Nome da empresa como a equipe conhece.',
  cnpj: 'Com ou sem pontuação. É por ele que a empresa é achada; sem CNPJ, pelo nome exato.',
  razao_social: 'Opcional. Como está no CNPJ.',
  telefone: 'Com DDD.',
  email: 'E-mail da empresa.',
  site: 'Endereço do site.',
  endereco: 'Rua e número.',
  bairro: 'Bairro.',
  cidade: 'Cidade.',
  uf: 'Duas letras, como MG.',
  cep: 'Oito números, com ou sem traço.',
  vendedor_email:
    'E-mail de um usuário ativo. Os contatos da empresa passam para ele. Em branco = não muda o dono.',
  etiquetas: 'Nomes separados por ponto e vírgula. Etiqueta inexistente é ignorada.',
  observacoes: 'Texto livre.',
}

// ─────────────────────────── a prévia, para os dois ───────────────────────────

/**
 * Casamento na importação: contato por documento e depois por telefone
 * (decisão 10.5); empresa por CNPJ e depois pelo nome exato. Encontrado
 * atualiza, não encontrado cria.
 */
export interface LinhaPrevia {
  linha: number
  nome: string
  documento: string | null
  telefone: string | null
  acao: 'criar' | 'atualizar' | 'erro'
  /** Só em 'atualizar': qual registro será alterado. */
  contatoId?: string
  casadoPor?: 'documento' | 'telefone' | 'cnpj' | 'nome'
  erros: string[]
  avisos: string[]
}

export interface PreviaImportacao {
  /** Guardado no servidor por alguns minutos, para confirmar sem reenviar o arquivo. */
  token: string
  totalLinhas: number
  criar: number
  atualizar: number
  comErro: number
  colunasIgnoradas: string[]
  linhas: LinhaPrevia[]
  expiraEm: string
}

export const confirmarImportacaoSchema = z.object({
  token: z.string().min(10),
})

export interface ResultadoImportacao {
  criados: number
  atualizados: number
  ignorados: number
}
